'use client';
import React, { useState, useEffect, useRef } from 'react';

const faqs = [
  {
    q: 'What is Movio Mobility Operating System?',
    a: "Movio is a white-label mobility SaaS platform and taxi booking software that enables taxi operators, logistics companies, food delivery businesses, and home service providers to launch and manage their own branded mobile applications across India.",
  },
  {
   q: 'How long does it take to launch a white-label taxi or delivery app with Movio?',
    a: 'With Movio, you can go live in as little as 2–4 weeks. Our white-label platform comes pre-built with customer apps, driver apps, admin dashboards, and payment integrations — all customized with your brand.',
  },
  {
    q: 'Do I need technical knowledge to use Movio?',
    a: 'No. Movio is designed for business operators, not developers. Our team handles all technical setup, app deployment, and onboarding. You focus on running your business.',
  },
  {
    q: 'Which cities and states does Movio operate in?',
    a: 'Movio is currently operational in 22+ cities across Tamil Nadu, Kerala, Karnataka, and Chhattisgarh, with active pan-India expansion underway.',
  },
  {
    q: 'Which business types can I launch using Movio?',
    a: 'Yes. Movio offers 9 platform products including Fleet Services, Logistics, Food Delivery, Grocery, Facility Pro, Single Store, Multi-Vendor Marketplace, and Delivery Integrations — all on one platform.',
  },
  {
    q: 'What is the pricing model for Movio?',
    a: 'Movio follows a flexible, pay-as-you-grow SaaS model with no heavy upfront investment. Pricing is customized based on the products you choose, the number of cities you operate in, and your fleet size — ensuring you only pay for what you actually use. To get a tailored quote aligned with your business needs, reach out to our team for a personalized consultation.',
  },
  {
    q: 'Is Movio suitable for Tier 2 and Tier 3 cities?',
    a: 'Absolutely. Movio is specifically built for Tier 2, Tier 3, and Tier 4 Indian markets with local language support, regional pricing flexibility, and operational workflows designed for Indian businesses.',
  },
  {
    q: "How does Movio's white-label platform work?",
    a: 'White-label means your customers see your brand name, logo, and colors — not Movio. You get fully branded Android and iOS apps, a custom domain, and complete ownership of your customer relationships.',
  },
  {
  q: 'Why should local operators choose Movio?',
  a: 'Movio helps local taxi operators, logistics companies, delivery businesses, and entrepreneurs launch their own branded digital platforms with white-label technology, AI-powered operations, and scalable SaaS infrastructure built for Tier 2 and Tier 3 cities.',
},
{
  q: 'Which industries does Movio support?',
  a: 'Movio supports taxi services, logistics, courier delivery, food delivery, grocery delivery, home services, fleet management, single-store businesses, and multi-vendor marketplaces.',
},
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a,
    },
  })),
};

interface FAQItemProps {
  faq: { q: string; a: string };
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
}

function FAQItem({ faq, index, isOpen, onToggle }: FAQItemProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={`bg-white rounded-2xl border transition-all duration-300 ${
        isOpen
          ? 'border-[rgba(0, 102, 255,0.3)] shadow-sm'
          : 'border-[rgba(15,23,42,0.08)]'
      }`}
    >
      <h3 className="m-0">
        <button
          type="button"
          className="w-full flex items-center justify-between p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066FF] focus-visible:ring-offset-2 rounded-2xl"
          onClick={() => onToggle(index)}
          aria-expanded={isOpen}
          aria-controls={`faq-answer-${index}`}
          id={`faq-question-${index}`}
        >
          <span className="font-semibold text-[#0F172A] pr-4 text-base leading-snug">
            {faq.q}
          </span>
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
              isOpen ? 'bg-[#0066FF] text-white' : 'bg-[#FFFFFF] text-[#64748B]'
            }`}
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
              width={16}
              height={16}
              className={`transition-transform duration-300 ${isOpen ? 'rotate-45' : 'rotate-0'}`}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </div>
        </button>
      </h3>

      <div
        id={`faq-answer-${index}`}
        role="region"
        aria-labelledby={`faq-question-${index}`}
        ref={contentRef}
        style={{
          maxHeight: isOpen ? `${contentRef.current?.scrollHeight ?? 500}px` : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.35s ease, opacity 0.3s ease',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className="px-6 pb-6 pt-0">
          <p className="text-[#475569] leading-relaxed">
            {faq.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <section className="py-24 bg-[#FFFFFF]" aria-label="Frequently Asked Questions About Movio" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll stagger-1">
            <span className="section-label block mb-3">FAQ</span>
            <h2 className="v2-h2 text-[#0F172A] mb-4">
              Frequently Asked{' '}
              <span className="text-[#0066FF]">Questions About Movio.</span>
            </h2>
<p className="text-[#475569] text-lg max-w-2xl mx-auto">
  Explore answers to common questions about Movio, India's Mobility Operating System. Learn how our white-label SaaS platform helps local operators and entrepreneurs launch taxi, logistics, food delivery, grocery delivery, and home service businesses across India.
</p>          </div>

          <div className="space-y-3" role="list">
            {faqs.map((faq, i) => (
              <div key={i} role="listitem">
                <FAQItem
                  faq={faq}
                  index={i}
                  isOpen={openIndex === i}
                  onToggle={handleToggle}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}